// Вертикальные столбики по дням / месяцам. SVG, без зависимостей.
// Hover показывает значение в tooltip над столбиком.

import { useState } from 'react';
import { formatWithCurrency } from '../../lib/money';

export type BarPoint = {
  bucket: string; // YYYY-MM-DD или YYYY-MM
  label: string; // короткий лейбл оси: "7", "Янв"
  fullLabel: string; // полный для tooltip: "7 мая"
  valueMinor: string;
};

type Props = {
  points: BarPoint[];
  currency: string;
  color: string; // CSS variable или hex
};

const HEIGHT = 160;
const TOP_PADDING = 18; // место под tooltip
const BOTTOM_PADDING = 22; // место под подписи
const MIN_BAR = 2; // минимальная высота столбика, чтобы что-то было видно

export function BarChart({ points, currency, color }: Props): JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);

  if (points.length === 0) {
    return <div className="bar-chart empty">Нет данных за период</div>;
  }

  const values = points.map((p) => Number(BigInt(p.valueMinor) / 100n));
  const maxVal = Math.max(...values, 1);
  const chartH = HEIGHT - TOP_PADDING - BOTTOM_PADDING;

  // Шаг по оси X — отметки только на части подписей, чтобы не наслаивались.
  const labelStep = points.length <= 7 ? 1 : points.length <= 14 ? 2 : Math.ceil(points.length / 8);

  return (
    <div className="bar-chart">
      <svg
        viewBox={`0 0 ${points.length * 12} ${HEIGHT}`}
        preserveAspectRatio="none"
        width="100%"
        height={HEIGHT}
        className="bar-svg"
      >
        {points.map((p, i) => {
          const v = values[i] ?? 0;
          const barH = v > 0 ? Math.max((v / maxVal) * chartH, MIN_BAR) : 0;
          const x = i * 12 + 2;
          const y = TOP_PADDING + (chartH - barH);
          const isHovered = hovered === i;
          const showLabel = i % labelStep === 0 || i === points.length - 1;
          return (
            <g key={p.bucket}>
              {/* Прозрачная hit-area на всю высоту — удобнее ловить тач */}
              <rect
                x={x - 1}
                y={0}
                width={10}
                height={HEIGHT}
                fill="transparent"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onTouchStart={() => setHovered(i)}
                style={{ cursor: 'pointer' }}
              />
              <rect
                x={x}
                y={y}
                width={8}
                height={barH}
                rx={2}
                fill={isHovered ? 'var(--accent)' : color}
                style={{ transition: 'fill 0.12s ease' }}
                pointerEvents="none"
              />
              {showLabel ? (
                <text
                  x={x + 4}
                  y={HEIGHT - 6}
                  fontSize="9"
                  textAnchor="middle"
                  fill="var(--ink-3)"
                  pointerEvents="none"
                >
                  {p.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {hovered != null && points[hovered] ? (
        <BarTooltip
          point={points[hovered]}
          currency={currency}
          x={hovered}
          total={points.length}
        />
      ) : null}
    </div>
  );
}

function BarTooltip({
  point,
  currency,
  x,
  total,
}: {
  point: BarPoint;
  currency: string;
  x: number;
  total: number;
}): JSX.Element {
  // Позиция в процентах от ширины — чтобы попасть точно над столбиком.
  const leftPct = ((x + 0.5) / total) * 100;
  // Прижимаем к краям, если у границы.
  const clampedLeft = Math.max(8, Math.min(92, leftPct));
  return (
    <div
      className="bar-tooltip"
      style={{ left: `${clampedLeft}%`, transform: 'translateX(-50%)' }}
    >
      <div className="bar-tooltip-label">{point.fullLabel}</div>
      <div className="bar-tooltip-value">
        {formatWithCurrency(point.valueMinor, currency)}
      </div>
    </div>
  );
}
